import { audio } from '../../../../audio/AudioManager.js';
import { SaveManager } from '../../../../state/SaveManager.js';
import { applicationLifecycle } from '../../../shared/lifecycle/index.js';
import { ensureMinimumTextTouchTarget } from '../../../ui/TouchTarget.js';
import { resolveStoryForestLayout, STORY_FOREST_LAYOUT_HEIGHT, STORY_FOREST_LAYOUT_WIDTH } from '../components/StoryForestLayout.js';
import { STORY_FOREST_ASSETS } from '../StoryForestAssets.js';
import {
  STORY_FOREST_DISTRICTS,
  completeStoryForestDistrictProgress,
  getStoryForestState,
  saveStoryForestDistrictProgress,
  visitStoryForestDistrict,
} from '../StoryForestState.js';
import { CHARACTER_GROVE_ASSETS } from '../minigames/character-grove/CharacterGroveAssets.js';
import {
  GRAMMAR_GLADE_ASSETS,
  GRAMMAR_GLADE_ASSET_LIST,
  canEnterGrammarGlade,
  getMissingGrammarGladeAssets,
} from './GrammarGladeAssets.js';
import {
  GRAMMAR_GLADE_CHALLENGES,
  GRAMMAR_GLADE_RESTORATION_EVENT,
  type GrammarRole,
} from './GrammarGladeConfig.js';
import { GrammarGladeState, type GrammarGladeSerializedState } from './GrammarGladeState.js';
import { canEnterFluencyLanternPath } from '../fluency-lantern-path/FluencyLanternAssets.js';

export const GRAMMAR_GLADE_SCENE_KEY = 'GrammarGlade';
const FONT_READING = '"Nunito", "Segoe UI", Arial, sans-serif';

interface GrammarGladeProgress extends GrammarGladeSerializedState { readonly restored: boolean; }
interface TileView { readonly image: Phaser.GameObjects.Image; readonly label: Phaser.GameObjects.Text; readonly badge: Phaser.GameObjects.Text; }
interface ArtButton { readonly image: Phaser.GameObjects.Image; readonly label: Phaser.GameObjects.Text; }

export class GrammarGladeScene extends Phaser.Scene {
  static readonly requiredImageKeys = GRAMMAR_GLADE_ASSET_LIST.map(asset => asset.key);

  private state!: GrammarGladeState;
  private dormantBackground!: Phaser.GameObjects.Image;
  private restoredBackground!: Phaser.GameObjects.Image;
  private missionPanel!: Phaser.GameObjects.Image;
  private cosmo!: Phaser.GameObjects.Image;
  private titleText!: Phaser.GameObjects.Text;
  private objectiveText!: Phaser.GameObjects.Text;
  private instructionText!: Phaser.GameObjects.Text;
  private feedbackText!: Phaser.GameObjects.Text;
  private controlsText!: Phaser.GameObjects.Text;
  private backText!: Phaser.GameObjects.Text;
  private homeText!: Phaser.GameObjects.Text;
  private nextText!: Phaser.GameObjects.Text;
  private sourceTiles: TileView[] = [];
  private sentenceTiles: TileView[] = [];
  private roleButtons: ArtButton[] = [];
  private punctuationButtons: ArtButton[] = [];
  private actionButtons: ArtButton[] = [];
  private restored = false;
  private leaving = false;
  private suspended = false;
  private reducedMotion = false;
  private renderScale = 1;
  private resizeTimer?: number;
  private unsubscribeLifecycle?: () => void;

  constructor() { super({ key: GRAMMAR_GLADE_SCENE_KEY }); }

  create() {
    this.sourceTiles = [];
    this.sentenceTiles = [];
    this.roleButtons = [];
    this.punctuationButtons = [];
    this.actionButtons = [];
    this.leaving = false;
    this.suspended = false;

    const world = getStoryForestState();
    const missing = getMissingGrammarGladeAssets(this.textures);
    if (!world.unlockedDistricts.includes(STORY_FOREST_DISTRICTS.grammarGlade)
      || !canEnterGrammarGlade(this.textures)) {
      if (missing.length > 0) console.error('[GrammarGlade] Required production textures are missing:', missing.map(asset => `${asset.key} (${asset.path})`));
      this.scene.start('AuthorsCabin');
      return;
    }
    void audio.enterSoundscape('story-forest');
    this.renderScale = this.configureRenderer();
    this.reducedMotion = SaveManager.getSettings().reducedMotion
      || (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    this.restored = world.completedDistricts.includes(STORY_FOREST_DISTRICTS.grammarGlade);
    this.restoreProgress(world.districtProgress[STORY_FOREST_DISTRICTS.grammarGlade]);
    visitStoryForestDistrict(STORY_FOREST_DISTRICTS.grammarGlade);
    SaveManager.setLastScene(GRAMMAR_GLADE_SCENE_KEY);
    SaveManager.save();
    this.buildEnvironment();
    this.buildInterface();
    this.registerInput();
    this.registerLifecycle();
    this.refresh();
    this.applyTextResolution();
    this.cameras.main.fadeIn(this.reducedMotion ? 150 : 440, 76, 112, 64);
  }

  private restoreProgress(value: unknown) {
    const progress = value as Partial<GrammarGladeProgress> | undefined;
    this.state = new GrammarGladeState(GRAMMAR_GLADE_CHALLENGES, progress?.version === 1 ? progress : undefined);
    if (progress?.version === 1) this.restored = progress.restored === true;
  }

  private buildEnvironment() {
    this.dormantBackground = this.add.image(640, 360, GRAMMAR_GLADE_ASSETS.dormant.key).setDisplaySize(1280, 720).setDepth(0);
    this.restoredBackground = this.add.image(640, 360, GRAMMAR_GLADE_ASSETS.restored.key).setDisplaySize(1280, 720).setDepth(1);
    this.missionPanel = this.add.image(640, 105, STORY_FOREST_ASSETS.storyMissionPanel.key).setDepth(30);
    this.cosmo = this.add.image(100, 650, STORY_FOREST_ASSETS.storyForestCosmo.key).setDepth(105);
  }

  private buildInterface() {
    this.titleText = this.makeText('GRAMMAR GLADE', 29, '#fff2b8', 'bold').setStroke('#173f2b', 7).setDepth(110);
    this.objectiveText = this.makeText('', 16, '#3e3028', 'bold').setWordWrapWidth(760).setDepth(40);
    this.instructionText = this.makeText('', 14, '#fff8dc', 'bold').setStroke('#173f2b', 5).setDepth(110);
    this.feedbackText = this.makeText('', 13, '#fff8dc', 'bold').setStroke('#173f2b', 5).setWordWrapWidth(780).setDepth(110);
    this.controlsText = this.makeText('1–5 select • Q/W/E/T/Y place • N/V/A label • . ? ! finish • Enter check • H hint • R reset • M Home', 9, '#fff8dc', 'bold').setStroke('#173f2b', 5).setDepth(110);
    this.backText = this.makeText('←  AUTHOR\'S CABIN', 13, '#ffffff', 'bold').setStroke('#173f2b', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    this.homeText = this.makeText('HOME HUB  (M)', 13, '#ffffff', 'bold').setStroke('#173f2b', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    this.nextText = this.makeText('FLUENCY LANTERN PATH • NEXT', 14, '#fff2b8', 'bold').setStroke('#173f2b', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    [this.backText, this.homeText, this.nextText].forEach(target => ensureMinimumTextTouchTarget(target));
    this.backText.on('pointerup', this.returnToCabin, this);
    this.homeText.on('pointerup', this.returnHome, this);
    this.nextText.on('pointerup', this.tryNextDistrict, this);

    const maxWords = Math.max(...GRAMMAR_GLADE_CHALLENGES.map(challenge => challenge.words.length));
    for (let index = 0; index < maxWords; index += 1) {
      const source = this.makeTile();
      source.image.on('pointerup', () => this.selectSource(index));
      this.sourceTiles.push(source);
      const slot = this.makeTile();
      slot.image.on('pointerup', () => this.activateSentenceSlot(index));
      this.sentenceTiles.push(slot);
    }
    this.roleButtons = [
      this.makeButton('NOUN', () => this.assignRole('noun')),
      this.makeButton('VERB', () => this.assignRole('verb')),
      this.makeButton('ADJECTIVE', () => this.assignRole('adjective')),
    ];
    this.punctuationButtons = ['.', '?', '!'].map(mark => this.makeButton(mark, () => this.choosePunctuation(mark)));
    this.actionButtons = [
      this.makeButton('CHECK', () => this.check()),
      this.makeButton('HINT', () => this.hint()),
      this.makeButton('RESET', () => this.reset()),
    ];
  }

  private makeText(value: string, size: number, color: string, style: 'normal' | 'bold') {
    return this.add.text(0, 0, value, { fontFamily: FONT_READING, fontSize: `${size}px`, fontStyle: style, color, align: 'center', lineSpacing: 3 }).setOrigin(0.5);
  }

  private makeTile(): TileView {
    const image = this.add.image(0, 0, GRAMMAR_GLADE_ASSETS.wordLeaf.key).setDepth(60).setInteractive({ useHandCursor: true });
    const label = this.makeText('', 14, '#3e3028', 'bold').setDepth(62);
    const badge = this.makeText('', 9, '#fff8dc', 'bold').setStroke('#173f2b', 4).setDepth(63);
    return { image, label, badge };
  }

  private makeButton(label: string, action: () => void): ArtButton {
    const image = this.add.image(0, 0, CHARACTER_GROVE_ASSETS.connectButton.key).setDepth(100).setInteractive({ useHandCursor: true });
    const text = this.makeText(label, 12, '#fff8dc', 'bold').setStroke('#173f2b', 4).setDepth(101);
    image.on('pointerup', action);
    return { image, label: text };
  }

  private selectSource(index: number) {
    if (!this.canAct()) return;
    const id = this.state.availableWordIds()[index];
    if (!id || !this.state.selectWord(id)) return;
    this.feedbackText.setText('Word selected. Choose its place in the sentence path.');
    audio.click(); this.capture();
  }

  private activateSentenceSlot(index: number) {
    if (!this.canAct() || index >= this.state.challenge.words.length) return;
    if (this.state.snapshot.orderValidated) {
      const id = this.state.snapshot.placedWordIds[index];
      if (!id || !this.state.selectWord(id)) return;
      this.feedbackText.setText('Word selected. Mark it as the noun, verb, or adjective.');
    } else if (!this.state.activateSlot(index)) return;
    audio.click(); this.capture();
  }

  private assignRole(role: GrammarRole) {
    if (!this.canAct() || !this.state.assignRole(role)) {
      this.feedbackText.setText('Select a word on the sentence path before naming its job.');
      return;
    }
    audio.click(); this.feedbackText.setText(`That word is marked ${role.toUpperCase()}. You can revise it before checking.`); this.capture();
  }

  private choosePunctuation(mark: string) {
    if (!this.canAct() || !this.state.choosePunctuation(mark)) return;
    audio.click(); this.feedbackText.setText(`You chose ${mark} for the sentence ending.`); this.capture();
  }

  private check() {
    if (this.restored) return this.replay();
    if (!this.canAct()) return;
    if (this.state.snapshot.complete && this.state.hasNextChallenge) {
      this.state.nextChallenge();
      this.feedbackText.setText(this.state.challenge.arrival);
      audio.click(); return this.capture();
    }
    const result = this.state.check();
    if (result === 'incomplete') this.feedbackText.setText('Place every word on the path before checking.');
    else if (result === 'order-mismatch') this.feedbackText.setText('Those words do not make a complete sentence yet. Move them and try again.');
    else if (result === 'order-correct') this.feedbackText.setText('The sentence order works! Now select words and label the noun, verb, and adjective. Then choose punctuation.');
    else if (result === 'roles-incomplete') this.feedbackText.setText('Label one noun, one verb, and one adjective, then choose the ending mark.');
    else if (result === 'roles-mismatch') this.feedbackText.setText('One word has the wrong job. A noun names, a verb shows action, and an adjective describes. Revise your labels.');
    else if (result === 'punctuation-mismatch') this.feedbackText.setText('The word jobs are right. Think about whether this sentence tells, asks, or shows strong feeling.');
    if (result !== 'complete') {
      if (result !== 'order-correct') audio.wrong(); else audio.click();
      return this.capture();
    }
    SaveManager.recordAnswer(this.state.challenge.skillId, true);
    this.feedbackText.setText(this.state.challenge.successMessage);
    if (this.state.mastered) {
      this.restored = true;
      completeStoryForestDistrictProgress(
        STORY_FOREST_DISTRICTS.grammarGlade,
        this.progressSnapshot(),
        GRAMMAR_GLADE_RESTORATION_EVENT,
        STORY_FOREST_DISTRICTS.fluencyLanternPath,
      );
      this.playRestoration(); audio.restoration();
    } else audio.correct();
    this.capture();
  }

  private hint() { if (this.canAct()) { this.feedbackText.setText(this.state.requestHint()); audio.hint(); this.capture(); } }
  private reset() { if (this.canAct() && this.state.resetCurrent()) { this.feedbackText.setText('The words return to the glade. Saved mastery stays safe.'); audio.click(); this.capture(); } }
  private replay() { this.restored = false; this.state.replay(); this.feedbackText.setText('Replay mode: relight the Grammar Glade without changing saved mastery.'); audio.click(); this.capture(); }
  private capture() { saveStoryForestDistrictProgress(STORY_FOREST_DISTRICTS.grammarGlade, this.progressSnapshot()); this.refresh(); }
  private progressSnapshot(): GrammarGladeProgress { return { ...this.state.serialize(), restored: this.restored }; }

  private refresh() {
    const snapshot = this.state.snapshot;
    const challenge = this.state.challenge;
    this.restoredBackground.setAlpha(this.restored ? 1 : 0);
    this.dormantBackground.setAlpha(this.restored ? 0 : 1);
    this.objectiveText.setText(this.restored
      ? 'Complete sentences have restored every leaf lantern in Grammar Glade.'
      : `${challenge.arrival}\nSentence ${GRAMMAR_GLADE_CHALLENGES.indexOf(challenge) + 1} of ${GRAMMAR_GLADE_CHALLENGES.length}`);
    this.instructionText.setText(snapshot.orderValidated ? 'Select a placed word, name its job, and choose the sentence ending.' : 'Select a word leaf, then place it on the sentence path.');
    const available = this.state.availableWordIds();
    this.sourceTiles.forEach((view, index) => {
      const id = available[index]; const word = this.state.wordForId(id ?? null);
      const visible = !this.restored && !snapshot.orderValidated && Boolean(word);
      view.image.setVisible(visible).setAlpha(snapshot.selectedWordId === id ? 1 : 0.88).setScale(snapshot.selectedWordId === id ? 1.04 : 1);
      view.label.setVisible(visible).setText(word?.text ?? '');
      view.badge.setVisible(visible).setText(`${index + 1}`);
    });
    const assignedRole = (id: string | null) => (['noun', 'verb', 'adjective'] as const).find(role => snapshot.roleAssignments[role] === id);
    this.sentenceTiles.forEach((view, index) => {
      const id = snapshot.placedWordIds[index] ?? null; const word = this.state.wordForId(id);
      const visible = !this.restored && index < challenge.words.length;
      view.image.setVisible(visible).setAlpha(id ? 1 : 0.62).setScale(snapshot.selectedWordId === id ? 1.04 : 1);
      view.label.setVisible(visible).setText(word?.text ?? 'place word');
      view.badge.setVisible(visible).setText(assignedRole(id)?.toUpperCase() ?? `${index + 1}`);
    });
    this.roleButtons.forEach((button, index) => {
      const visible = !this.restored && snapshot.orderValidated;
      button.image.setVisible(visible); button.label.setVisible(visible);
      const role = (['noun', 'verb', 'adjective'] as const)[index];
      button.image.setAlpha(snapshot.roleAssignments[role] ? 1 : 0.8);
    });
    this.punctuationButtons.forEach((button, index) => {
      const mark = challenge.punctuationOptions[index]; const visible = !this.restored && snapshot.orderValidated;
      button.image.setVisible(visible); button.label.setVisible(visible).setText(mark);
      button.image.setAlpha(snapshot.selectedPunctuation === mark ? 1 : 0.75);
    });
    this.actionButtons.forEach((button, index) => {
      const visible = !this.restored || index === 0;
      button.image.setVisible(visible); button.label.setVisible(visible);
    });
    this.actionButtons[0].label.setText(this.restored ? 'REPLAY' : snapshot.complete && this.state.hasNextChallenge ? 'NEXT SENTENCE' : 'CHECK');
    this.controlsText.setVisible(!this.restored);
    this.instructionText.setVisible(!this.restored);
    this.nextText.setVisible(this.restored);
    if (!this.feedbackText.text) this.feedbackText.setText(challenge.arrival);
    this.layout();
  }

  private layout() {
    const { safe, compact } = resolveStoryForestLayout(this); const portrait = safe.width < 700;
    const panelWidth = portrait ? safe.width - 24 : Math.min(compact ? 760 : 880, safe.width - 190);
    this.missionPanel.setPosition(safe.centerX, safe.y + (portrait ? 115 : compact ? 91 : 100)).setDisplaySize(panelWidth, panelWidth * (1086 / 1448));
    this.titleText.setPosition(safe.centerX, safe.y + (portrait ? 43 : 26)).setFontSize(portrait ? 20 : compact ? 24 : 29);
    this.objectiveText.setPosition(safe.centerX, safe.y + (portrait ? 112 : compact ? 91 : 101)).setFontSize(portrait ? 11 : compact ? 13 : 15).setWordWrapWidth(panelWidth * 0.76);
    this.backText.setPosition(safe.x + (portrait ? 67 : 87), safe.y + 22).setFontSize(portrait ? 8 : compact ? 10 : 13);
    this.homeText.setPosition(safe.right - (portrait ? 52 : 66), safe.y + 22).setFontSize(portrait ? 8 : compact ? 10 : 13);
    this.instructionText.setPosition(safe.centerX, safe.y + (portrait ? 180 : compact ? 168 : 184)).setFontSize(portrait ? 10 : compact ? 12 : 14);
    const count = this.state.challenge.words.length;
    const gap = Math.min(portrait ? 118 : compact ? 142 : 158, (safe.width - 50) / count);
    const tileW = Math.min(portrait ? 112 : compact ? 132 : 146, gap - 6); const tileH = tileW;
    const row = (views: TileView[], y: number) => views.forEach((view, index) => {
      const x = safe.centerX + (index - (count - 1) / 2) * gap;
      view.image.setPosition(x, y).setDisplaySize(tileW, tileH);
      view.label.setPosition(x, y).setFontSize(portrait ? 10 : compact ? 12 : 14);
      view.badge.setPosition(x, y - tileH * 0.33).setFontSize(portrait ? 7 : 9);
    });
    row(this.sourceTiles, safe.y + (portrait ? 254 : compact ? 245 : 266));
    row(this.sentenceTiles, safe.y + (portrait ? 363 : compact ? 358 : 390));
    const roleY = safe.y + (portrait ? 448 : compact ? 452 : 492);
    this.roleButtons.forEach((button, index) => this.positionButton(button, safe.centerX + (index - 1) * (portrait ? 128 : 160), roleY, portrait ? 112 : 138, portrait ? 47 : 56));
    const punctY = safe.y + (portrait ? 500 : compact ? 512 : 552);
    this.punctuationButtons.forEach((button, index) => this.positionButton(button, safe.centerX + (index - 1) * (portrait ? 80 : 96), punctY, portrait ? 66 : 78, portrait ? 42 : 48));
    const actionY = safe.bottom - (portrait ? 48 : 42);
    this.actionButtons.forEach((button, index) => this.positionButton(button, safe.centerX + (index - 1) * (portrait ? 138 : 170), actionY, portrait ? 122 : 148, portrait ? 50 : 58));
    this.feedbackText.setPosition(safe.centerX, safe.bottom - (portrait ? 92 : 90)).setFontSize(portrait ? 9 : compact ? 11 : 13).setWordWrapWidth(portrait ? safe.width - 30 : Math.min(780, safe.width - 170));
    this.controlsText.setPosition(safe.centerX, safe.bottom - 10).setFontSize(portrait ? 6 : compact ? 8 : 9).setWordWrapWidth(safe.width - 20);
    this.nextText.setPosition(safe.centerX, safe.bottom - (portrait ? 113 : 90)).setFontSize(portrait ? 11 : 14);
    this.cosmo.setPosition(safe.x + (compact ? 50 : 66), safe.bottom - (compact ? 49 : 60)).setDisplaySize(compact ? 72 : 90, compact ? 96 : 120);
  }

  private positionButton(button: ArtButton, x: number, y: number, width: number, height: number) {
    button.image.setPosition(x, y).setDisplaySize(width, height); button.label.setPosition(x, y).setFontSize(width < 100 ? 14 : width < 125 ? 9 : 11);
  }

  private playRestoration() {
    const effect = this.add.image(640, 390, STORY_FOREST_ASSETS.storyMagicVfx.key).setDisplaySize(540, 540).setDepth(95).setBlendMode(Phaser.BlendModes.ADD).setAlpha(this.reducedMotion ? 0.72 : 0);
    if (this.reducedMotion) this.time.delayedCall(800, () => effect.destroy());
    else this.tweens.add({ targets: effect, alpha: 0.94, scaleX: 1.15, scaleY: 1.15, duration: 560, yoyo: true, hold: 160, onComplete: () => effect.destroy() });
  }

  private registerInput() { this.input.keyboard?.on('keydown', this.handleKeyDown, this); }
  private handleKeyDown(event: KeyboardEvent) {
    if (this.leaving || this.suspended) return;
    const key = event.key.toLocaleLowerCase();
    if (key === 'escape') return this.returnToCabin(); if (key === 'm') return this.returnHome();
    if (key === 'enter' || key === ' ') return this.check(); if (key === 'h') return this.hint(); if (key === 'r') return this.reset();
    const sourceIndex = ['1', '2', '3', '4', '5'].indexOf(key);
    if (sourceIndex >= 0) {
      if (this.state.snapshot.orderValidated) {
        const id = this.state.snapshot.placedWordIds[sourceIndex]; if (id) { this.state.selectWord(id); audio.click(); this.capture(); }
      } else this.selectSource(sourceIndex);
      return;
    }
    const slotIndex = ['q', 'w', 'e', 't', 'y'].indexOf(key); if (slotIndex >= 0) return this.activateSentenceSlot(slotIndex);
    if (key === 'n') return this.assignRole('noun'); if (key === 'v') return this.assignRole('verb'); if (key === 'a') return this.assignRole('adjective');
    if (['.', '?', '!'].includes(event.key)) this.choosePunctuation(event.key);
  }

  private registerLifecycle() {
    this.unsubscribeLifecycle = applicationLifecycle.subscribe({ onSuspend: () => { this.suspended = true; }, onResume: () => { this.suspended = false; } });
    window.addEventListener('resize', this.handleResize); this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
  }
  private canAct() { return !this.leaving && !this.suspended && !this.restored; }
  private configureRenderer() {
    const parentW = this.scale.parentSize.width || window.innerWidth; const parentH = this.scale.parentSize.height || window.innerHeight;
    const cssScale = Math.min(parentW / STORY_FOREST_LAYOUT_WIDTH, parentH / STORY_FOREST_LAYOUT_HEIGHT);
    const scale = Phaser.Math.Clamp(cssScale * Phaser.Math.Clamp(window.devicePixelRatio || 1, 1, 2), 1, 2);
    this.scale.scaleMode = Phaser.Scale.FIT; this.scale.autoCenter = Phaser.Scale.CENTER_BOTH; this.scale.displaySize.setAspectMode(Phaser.Scale.FIT);
    this.scale.setGameSize(Math.round(STORY_FOREST_LAYOUT_WIDTH * scale), Math.round(STORY_FOREST_LAYOUT_HEIGHT * scale)); this.scale.refresh(); this.cameras.main.setZoom(scale).centerOn(640, 360); return scale;
  }
  private applyTextResolution() { this.children.list.forEach(object => { if (object instanceof Phaser.GameObjects.Text) object.setResolution(this.renderScale); }); }
  private readonly handleResize = () => { if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer); this.resizeTimer = window.setTimeout(() => { if (!this.scene.isActive()) return; this.renderScale = this.configureRenderer(); this.layout(); this.applyTextResolution(); }, 80); };
  private readonly returnToCabin = () => { if (this.leaving) return; this.leaving = true; saveStoryForestDistrictProgress(STORY_FOREST_DISTRICTS.grammarGlade, this.progressSnapshot()); this.scene.start('AuthorsCabin'); };
  private readonly returnHome = () => { if (this.leaving) return; this.leaving = true; saveStoryForestDistrictProgress(STORY_FOREST_DISTRICTS.grammarGlade, this.progressSnapshot()); SaveManager.setLastScene(GRAMMAR_GLADE_SCENE_KEY); SaveManager.save(); this.scene.start('HomeHub'); };
  private readonly tryNextDistrict = () => {
    if (!this.restored) return;
    if (!canEnterFluencyLanternPath(this.textures)) { this.feedbackText.setText('The lantern path is unlocked. Fluency Lantern Path is awaiting its runtime QA gate.'); return audio.click(); }
    this.leaving = true; saveStoryForestDistrictProgress(STORY_FOREST_DISTRICTS.grammarGlade, this.progressSnapshot()); this.scene.start('FluencyLanternPath');
  };
  private shutdown() { if (this.state) saveStoryForestDistrictProgress(STORY_FOREST_DISTRICTS.grammarGlade, this.progressSnapshot()); this.unsubscribeLifecycle?.(); audio.leaveSoundscape({ id: 'story-forest' }); window.removeEventListener('resize', this.handleResize); if (this.resizeTimer !== undefined) clearTimeout(this.resizeTimer); this.input.keyboard?.off('keydown', this.handleKeyDown, this); this.tweens.killAll(); }
}
